/**
 * Explorer toolbar: counts, quick search, and the column / view / export / density controls.
 */

import { useRef, useState, type ReactNode } from 'react';
import { Button, SearchBar, Select, Icon, useTheme, spacing, fontSize } from '@fti/design-system';
import { Popover } from './Popover';
import type { Density } from './model/types';

interface ExplorerToolbarProps {
  shownRows: number;
  loadedRows: number;
  visibleColumns: number;
  totalColumns: number;
  totalCount?: number;
  quickSearch: string;
  onQuickSearch: (q: string) => void;
  density: Density;
  onDensity: (d: Density) => void;
  onOpenColumns: () => void;
  onExport: (format: 'csv' | 'json') => void;
  viewMenu: ReactNode;
}

export function ExplorerToolbar(props: ExplorerToolbarProps) {
  const { theme } = useTheme();
  const exportRef = useRef<HTMLButtonElement>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const muted = theme.colors.text.secondary;

  const rowsLabel =
    props.shownRows === props.loadedRows
      ? `${props.loadedRows.toLocaleString()} rows`
      : `${props.shownRows.toLocaleString()} of ${props.loadedRows.toLocaleString()} rows`;
  const colsLabel =
    props.visibleColumns === props.totalColumns
      ? `${props.totalColumns} columns`
      : `${props.visibleColumns} of ${props.totalColumns} columns`;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, flexWrap: 'wrap' }}>
      <span style={{ fontSize: fontSize.sm, color: theme.colors.text.primary, fontWeight: 600 }}>{rowsLabel}</span>
      <span style={{ fontSize: fontSize.sm, color: muted }}>· {colsLabel}</span>
      {props.totalCount !== undefined && (
        <span style={{ fontSize: fontSize.sm, color: muted }}>
          · {props.totalCount.toLocaleString()} total matches
        </span>
      )}

      <div style={{ flex: 1, minWidth: spacing.md }} />

      <div style={{ width: 260 }}>
        <SearchBar
          size="sm"
          value={props.quickSearch}
          onValueChange={props.onQuickSearch}
          placeholder="Search all fields…"
          aria-label="Search all fields"
        />
      </div>

      <Button variant="outline" size="sm" leftIcon={<Icon name="menu" size={14} />} onClick={props.onOpenColumns}>
        Columns
      </Button>

      {props.viewMenu}

      <Button
        ref={exportRef}
        variant="outline"
        size="sm"
        leftIcon={<Icon name="download" size={14} />}
        onClick={() => setExportOpen((o) => !o)}
      >
        {`Export ${props.shownRows.toLocaleString()} rows`}
      </Button>
      <Popover anchor={exportRef.current} open={exportOpen} onClose={() => setExportOpen(false)} placement="bottom-end" width={240}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
          <Button variant="ghost" size="sm" onClick={() => { props.onExport('csv'); setExportOpen(false); }}>
            CSV — visible columns, flattened
          </Button>
          <Button variant="ghost" size="sm" onClick={() => { props.onExport('json'); setExportOpen(false); }}>
            JSON — original records
          </Button>
        </div>
      </Popover>

      <div style={{ width: 170 }}>
        <Select
          size="sm"
          value={props.density}
          onChange={(v) => props.onDensity(v as Density)}
          options={[
            { value: 'compact', label: 'Compact' },
            { value: 'comfortable', label: 'Comfortable' },
          ]}
        />
      </div>
    </div>
  );
}
